import { useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
import useRuntimeConfig from '@vl-core/hooks/useConfig';
import { Button, Form, Input } from 'antd';
import AsyncButton from '@components/UI/AsyncButton/AsyncButton';
import styled from '@emotion/styled';

type MFAViewProps = {
  onFinish: (values: Record<string, any>) => void;
  loading: boolean;
  resendCode: () => Promise<void>;
  onCancel: () => void;
};

const StyledActions = styled.div`
  display: flex;
  justify-content: end;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const StyledTimeLeft = styled.p`
  min-height: 1.5em;
  margin: 0.5rem 0 0;
  text-align: end;
  font-variant-numeric: tabular-nums;
`;

const MFAView: FC<MFAViewProps> = ({ onFinish, loading, resendCode, onCancel }) => {
  const { t } = useTranslation('common');
  const { MFA_LENGTH, MFA_SECS } = useRuntimeConfig();
  const [mfaForm] = Form.useForm();

  useEffect(() => {
    function windowFocused() {
      // @ts-ignore
      window.code?.focus();
    }
    window.addEventListener('focus', windowFocused);

    return () => window.removeEventListener('focus', windowFocused);
  }, []);

  return (
    <div className="login-card">
      <p>
        {t('We’ve sent you an email or text message with a ')}
        {MFA_LENGTH}
        {t('-digit security code using your preferred contact method (email or text message)')}
      </p>
      <Form form={mfaForm} layout="vertical" scrollToFirstError={{ focus: true, block: 'center' }} {...{ onFinish }}>
        <Form.Item
          label={t('Code')}
          name="code"
          validateTrigger="onSubmit"
          rules={[
            {
              required: true,
              message: `Please input your ${MFA_LENGTH} digit code!`
            }
          ]}
        >
          <Input data-testid="login-code-input" autoComplete="off" autoFocus inputMode="numeric" />
        </Form.Item>

        <AsyncButton
          size="large"
          htmlType="button"
          onClick={resendCode}
          disableInitially
          delay={Number(MFA_SECS) || 60}
          data-testid="resend-code"
        >
          <StyledActions>
            <Button type="default" size="large" htmlType="button" onClick={onCancel} data-testid="cancel">
              {t('Cancel')}
            </Button>

            <AsyncButton.Button data-testid="resent">{t('Resend code')}</AsyncButton.Button>

            <Button type="primary" size="large" htmlType="submit" {...{ loading }} data-testid="submit">
              {t('Submit')}
            </Button>
          </StyledActions>

          <StyledTimeLeft>
            <AsyncButton.TimeLeft data-testid="countdown" type="Email/SMS" />
          </StyledTimeLeft>
        </AsyncButton>
      </Form>
    </div>
  );
};

export default MFAView;
